"use client";

/**
 * Which rows didn't import, and why.
 *
 * WHY THIS EXISTS: the import list has always shown "103 errors" with no way to
 * see WHICH rows or WHY. The only recourse was scrolling the lead list looking
 * for people who weren't there — which doesn't work, because a row that failed
 * never became a lead.
 *
 * The backend already stored everything needed (source_row_number, message, and
 * the full original row in raw_data_snapshot) and exposed
 * GET /leads/imports/:id/failed-results. Nothing ever called it.
 *
 * Leads with the reason SUMMARY, because these failures cluster hard — 103 rows
 * failing for one reason is one problem to fix, not 103.
 */
import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { AlertTriangle, Download, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";

// Failure messages can contain internal storage URLs with signed tokens
// (older rows stored them verbatim) — never render or export those.
const scrubMessage = (m: string | null | undefined): string =>
  (m || "")
    .replace(/https?:\/\/\S+/gi, "[file]")
    .replace(/token=[\w.-]+/gi, "token=[redacted]");


interface FailedRow {
  id: string;
  sourceRowNumber: number | null;
  message: string | null;
  rawDataSnapshot?: Record<string, any> | null;
}

/** Fields worth showing so a person can recognise the record in their sheet. */
const IDENTITY_FIELDS = ["name", "phone", "email", "city", "state"];

export default function ImportErrorsDialog({
  open,
  onOpenChange,
  importId,
  fileName,
  failedCount,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  importId: string | null;
  fileName?: string;
  failedCount?: number;
}) {
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["import-failed-results", importId, showAll],
    enabled: open && !!importId,
    queryFn: async () => {
      const res: any = await api.get({
        url: getApiUrl(endpoint.leads.getImportFailedResults(importId!, showAll ? 500 : 100)),
      });
      return (res?.data ?? res) as {
        totalFailed: number;
        hasMore: boolean;
        reasons: { reason: string; count: number }[];
        results: FailedRow[];
      };
    },
  });

  const rows = data?.results ?? [];
  const total = data?.totalFailed ?? failedCount ?? 0;

  /** Every column present across the failed rows, so nothing is silently dropped. */
  const allKeys = useMemo(() => {
    const k = new Set<string>();
    rows.forEach((r) => Object.keys(r.rawDataSnapshot || {}).forEach((x) => k.add(x)));
    return [...k];
  }, [rows]);

  /**
   * Download the failed rows as CSV — the ORIGINAL columns plus the reason, so
   * the file can be corrected and re-uploaded directly. Without this the only
   * fix is hunting them down by hand in the source spreadsheet.
   */
  const downloadCsv = () => {
    const esc = (v: any) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["Row", "Reason", ...allKeys];
    const lines = [header.map(esc).join(",")];
    rows.forEach((r) => {
      lines.push(
        [r.sourceRowNumber ?? "", scrubMessage(r.message), ...allKeys.map((k) => r.rawDataSnapshot?.[k])]
          .map(esc)
          .join(","),
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `failed-rows-${(fileName || "import").replace(/\.csv$/i, "")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[860px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            {total.toLocaleString()} row{total === 1 ? "" : "s"} didn&apos;t import
          </DialogTitle>
          {fileName && <p className="text-xs text-gray-500 truncate">{fileName}</p>}
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-10 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading the rows that failed…
          </div>
        )}

        {isError && (
          <p className="py-8 text-center text-sm text-gray-600">
            Couldn&apos;t load the failed rows. Try again in a moment.
          </p>
        )}

        {!isLoading && !isError && (
          <>
            {/* Reason summary first — these cluster, so one fix usually clears
                most of the file. */}
            {(data?.reasons?.length ?? 0) > 0 && (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-1">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Why they failed</p>
                {data!.reasons.slice(0, 5).map((r) => (
                  <div key={scrubMessage(r.reason)} className="flex items-start gap-2 text-[12px]">
                    <span className="tabular-nums font-medium text-red-700 shrink-0">{r.count.toLocaleString()}×</span>
                    <span className="text-gray-700">{scrubMessage(r.reason)}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex-1 overflow-auto rounded-md border border-gray-200">
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 bg-gray-50 border-b">
                  <tr className="text-left text-gray-500">
                    <th className="px-2 py-1.5 font-medium w-14">Row</th>
                    {IDENTITY_FIELDS.map((f) => (
                      <th key={f} className="px-2 py-1.5 font-medium capitalize">{f}</th>
                    ))}
                    <th className="px-2 py-1.5 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50 align-top">
                      <td className="px-2 py-1.5 tabular-nums text-gray-400">{r.sourceRowNumber ?? "—"}</td>
                      {IDENTITY_FIELDS.map((f) => (
                        <td key={f} className="px-2 py-1.5 text-gray-800 max-w-[130px] truncate"
                            title={String(r.rawDataSnapshot?.[f] ?? "")}>
                          {String(r.rawDataSnapshot?.[f] ?? "—")}
                        </td>
                      ))}
                      <td className="px-2 py-1.5 text-red-700">{scrubMessage(r.message) || "Unknown error"}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={IDENTITY_FIELDS.length + 2} className="px-2 py-8 text-center text-gray-500">
                      No failed rows recorded for this import.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between pt-1">
              <p className="text-[11px] text-gray-500">
                Showing {rows.length.toLocaleString()} of {total.toLocaleString()}
                {data?.hasMore && !showAll && (
                  <button className="ml-2 text-primary hover:underline" onClick={() => setShowAll(true)}>
                    show more
                  </button>
                )}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5"
                        onClick={downloadCsv} disabled={rows.length === 0}>
                  <Download className="h-3.5 w-3.5" /> Download these rows
                </Button>
                <Button size="sm" className="h-7 text-xs" onClick={() => onOpenChange(false)}>Close</Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
